import { CSSProperties, ReactNode, useEffect, useState } from "react";
import {
  callPrintServer, DEFAULT_PRINTER_SETTINGS, discoverPrinters, DiscoverResult, loadPrinterSettings, PrinterSettings,
  printerPayload, savePrinterSettings,
} from "../utils/printerSettings";
import { buildReceiptBytes, isAndroid, printViaRawBT, TEST_RECEIPT } from "../utils/escpos";

interface Props {
  onClose: () => void;
  /** Kaydedilince (ör. bekleyen yazdırmayı sürdürmek için) */
  onSaved?: (s: PrinterSettings) => void;
}

const label: CSSProperties = { fontSize: ".78rem", fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 };
const hint: CSSProperties = { fontSize: ".72rem", color: "#9ca3af", marginTop: 4 };
const input: CSSProperties = { width: "100%", boxSizing: "border-box", padding: "9px 12px", borderRadius: 10, border: "1.5px solid #e5e7eb", fontSize: ".9rem", outline: "none" };

function Field({ title, help, children }: { title: string; help?: ReactNode; children: ReactNode }) {
  return (
    <div>
      <span style={label}>{title}</span>
      {children}
      {help && <div style={hint}>{help}</div>}
    </div>
  );
}

function Chips<T extends string | number>({ value, options, onChange }: { value: T; options: { v: T; label: string }[]; onChange: (v: T) => void }) {
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {options.map((o) => (
        <button key={String(o.v)} type="button" onClick={() => onChange(o.v)}
          style={{
            padding: "7px 12px", borderRadius: 9, fontSize: ".8rem", fontWeight: 700, cursor: "pointer",
            border: `1.5px solid ${value === o.v ? "#111827" : "#e5e7eb"}`,
            background: value === o.v ? "#111827" : "#fff", color: value === o.v ? "#fff" : "#374151",
          }}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function ScanOption({ icon, title, sub, selected, onSelect }: { icon: string; title: string; sub: string; selected: boolean; onSelect: () => void }) {
  return (
    <button type="button" onClick={onSelect}
      style={{
        display: "flex", alignItems: "center", gap: 10, textAlign: "left", padding: "8px 10px", borderRadius: 10, cursor: "pointer",
        border: `1.5px solid ${selected ? "#111827" : "#e5e7eb"}`, background: selected ? "#111827" : "#fff", color: selected ? "#fff" : "#111827",
      }}>
      <i className={`bi ${icon}`}></i>
      <span style={{ flex: 1, minWidth: 0 }}>
        <b>{title}</b>
        <span style={{ display: "block", fontSize: ".74rem", opacity: 0.75 }}>{sub}</span>
      </span>
      {selected ? <i className="bi bi-check-circle-fill"></i> : <span style={{ fontSize: ".74rem", fontWeight: 700 }}>Seç</span>}
    </button>
  );
}

type Status = { kind: "ok" | "error" | "info"; text: string } | null;

/** Kasadaki fiş yazıcısının ayarları. Her bilgisayar kendi yazıcısını burada tanımlar. */
export default function PrinterSettingsModal({ onClose, onSaved }: Props) {
  const [s, setS] = useState<PrinterSettings>(() => {
    const loaded = loadPrinterSettings();
    // Android tablette ilk açılışta RawBT önerilir (yerel program Android'de çalışmaz)
    return isAndroid() && !loaded.host.trim() && !loaded.printerName.trim() ? { ...loaded, connection: "rawbt" } : loaded;
  });
  const [busy, setBusy] = useState<"" | "server" | "printer" | "test">("");
  const [status, setStatus] = useState<Status>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scan, setScan] = useState<DiscoverResult | null>(null);

  const runScan = async () => {
    setScanning(true);
    setScan(null);
    setStatus(null);
    const r = await discoverPrinters(s, Number(s.port) || 9100);
    setScan(r);
    // Tek bir aday varsa (ağda ya da USB'de) doğrudan seç
    const usb = r.windowsPrinters.filter((w) => w.usb);
    if (r.ok && r.printers.length === 1 && usb.length === 0) {
      setS((p) => ({ ...p, connection: "network", host: r.printers[0].host, port: r.printers[0].port }));
    } else if (r.ok && usb.length === 1 && r.printers.length === 0) {
      setS((p) => ({ ...p, connection: "windows", printerName: usb[0].name }));
    }
    setScanning(false);
  };

  // Yazıcı henüz tanımlı değilse pencere açılınca kendiliğinden ara
  useEffect(() => {
    if (s.connection !== "rawbt" && !isAndroid() && !s.host.trim() && !s.printerName.trim()) runScan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = <K extends keyof PrinterSettings>(k: K, v: PrinterSettings[K]) => setS((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const ipValid = /^(\d{1,3}\.){3}\d{1,3}$|^[a-zA-Z0-9.-]+$/.test(s.host.trim());

  const run = async (kind: "server" | "printer" | "test") => {
    setBusy(kind);
    setStatus(null);
    const r = kind === "server"
      ? await callPrintServer("/api/health", undefined, s, "GET")
      : kind === "printer"
        ? await callPrintServer("/api/printer/check", { printer: printerPayload(s) }, s)
        : await callPrintServer("/api/print/test", { printer: printerPayload(s) }, s);
    setStatus(r.ok
      ? { kind: "ok", text: kind === "server" ? "Yazdırma sunucusu çalışıyor." : kind === "printer" ? r.message : "Test fişi gönderildi. Türkçe harfler bozuksa \"Türkçe karakter\" ayarını değiştirin." }
      : { kind: "error", text: r.message });
    setBusy("");
  };

  const save = () => {
    savePrinterSettings(s);
    onSaved?.(s);
    onClose();
  };

  const isNetwork = s.connection === "network";
  const isRawbt = s.connection === "rawbt";
  const needsHost = isNetwork && !s.host.trim();
  /** Seçili bağlantı türü için hedef eksik ya da geçersiz mi */
  const targetInvalid = isRawbt ? false : isNetwork ? (!s.host.trim() || !ipValid) : !s.printerName.trim();
  const usbPrinters = scan?.windowsPrinters.filter((w) => w.usb) ?? [];
  const otherWindowsPrinters = scan?.windowsPrinters.filter((w) => !w.usb) ?? [];

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 1080, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, backdropFilter: "blur(3px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "#fff", borderRadius: 20, width: "min(96vw, 500px)", maxHeight: "92vh", overflowY: "auto", boxShadow: "0 8px 40px rgba(0,0,0,.2)" }}>
        <div style={{ padding: "18px 22px 14px", display: "flex", alignItems: "center", gap: 12, borderBottom: "1.5px solid #f1f5f9" }}>
          <span style={{ width: 38, height: 38, borderRadius: 11, background: "#111827", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <i className="bi bi-printer-fill"></i>
          </span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: "1rem", color: "#111827" }}>Yazıcı Ayarları</div>
            <div style={{ fontSize: ".74rem", color: "#9ca3af" }}>Bu bilgisayara özel · fiş yazıcınızın bilgilerini girin</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#9ca3af", fontSize: "1.1rem", cursor: "pointer" }} aria-label="Kapat">
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <div style={{ padding: "16px 22px", display: "flex", flexDirection: "column", gap: 14 }}>
          <Field title="Yazıcı nasıl bağlı?">
            <Chips value={s.connection} onChange={(v) => set("connection", v)} options={[
              { v: "network", label: "Ağ kablosu / Wi-Fi (IP)" },
              { v: "windows", label: "USB (bu bilgisayara)" },
              { v: "rawbt", label: "Android tablet (RawBT)" },
            ]} />
          </Field>

          {isRawbt ? (
            <div style={{ background: "#ecfdf5", border: "1.5px solid #a7f3d0", borderRadius: 12, padding: "10px 12px", fontSize: ".78rem", color: "#065f46", lineHeight: 1.55 }}>
              <b><i className="bi bi-android2 me-1"></i>Android tablette bilgisayar gerekmez.</b> Fiş tablette hazırlanır, <b>RawBT</b> uygulaması ağdaki yazıcıya basar. Tablete bir kez:
              <ol style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                <li>Play Store'dan <a href="https://play.google.com/store/apps/details?id=ru.a402d.rawbtprinter" target="_blank" rel="noopener noreferrer"><b>RawBT</b></a> uygulamasını kurun.</li>
                <li>RawBT → Ayarlar → Bağlantı yöntemi: <b>LAN (Wi-Fi/Ethernet)</b> → yazıcının IP'si (ör. 192.168.3.50), port <b>9100</b>.</li>
                <li>Aşağıdan <b>Test fişi yazdır</b>'a basın; Chrome "RawBT ile aç" derse <b>Her zaman</b> seçin.</li>
              </ol>
              <div style={{ marginTop: 6, color: "#92400e" }}>
                <i className="bi bi-exclamation-triangle me-1"></i>Yazıcı ile tablet aynı ağda olmalı (ör. ikisi de 192.168.3.x).
              </div>
            </div>
          ) : isNetwork ? (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 110px", gap: 10 }}>
              <Field title="Yazıcı IP adresi" help="Yazıcının kendi ayar fişinde yazar (yazıcı kapalıyken FEED tuşuna basılı tutup açın).">
                <input style={{ ...input, borderColor: s.host && !ipValid ? "#f87171" : undefined }} value={s.host} placeholder="192.168.1.50"
                  inputMode="decimal" onChange={(e) => set("host", e.target.value)} autoFocus={needsHost} />
              </Field>
              <Field title="Port" help="Genelde 9100">
                <input style={input} type="number" min={1} max={65535} value={s.port}
                  onChange={(e) => set("port", Number(e.target.value) || 9100)} />
              </Field>
            </div>
          ) : (
            <Field title="Windows yazıcısı" help="Yazıcının Windows'taki adı. Aşağıdaki aramadan seçebilirsiniz.">
              <input style={input} value={s.printerName} placeholder="ör. POS-80" list="printer-name-options"
                onChange={(e) => set("printerName", e.target.value)} />
              <datalist id="printer-name-options">
                {(scan?.windowsPrinters ?? []).map((w) => <option key={w.name} value={w.name} />)}
              </datalist>
            </Field>
          )}

          {/* Otomatik bulma */}
          {!isRawbt && (
          <div style={{ background: "#f8fafc", border: "1.5px dashed #cbd5e1", borderRadius: 12, padding: "10px 12px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: ".8rem", color: "#475569", flex: 1, minWidth: 180 }}>
                Yazıcıyı otomatik bulun: ağdaki ve bu bilgisayara USB ile bağlı fiş yazıcıları aranır.
              </span>
              <button type="button" className="btn btn-sm btn-dark" onClick={runScan} disabled={scanning}>
                {scanning ? <><span className="spinner-border spinner-border-sm me-1" />Aranıyor...</> : <><i className="bi bi-search me-1"></i>Yazıcı ara</>}
              </button>
            </div>

            {scan && !scan.ok && (
              <div style={{ marginTop: 8, fontSize: ".78rem", color: "#b91c1c" }}><i className="bi bi-exclamation-circle me-1"></i>{scan.message}</div>
            )}

            {scan?.ok && (scan.printers.length > 0 || usbPrinters.length > 0) && (
              <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
                {scan.printers.map((p) => (
                  <ScanOption key={p.host} icon="bi-ethernet" title={p.model || "Fiş yazıcısı (ağ)"} sub={`${p.host}:${p.port}`}
                    selected={isNetwork && s.host.trim() === p.host}
                    onSelect={() => setS((x) => ({ ...x, connection: "network", host: p.host, port: p.port }))} />
                ))}
                {usbPrinters.map((w) => (
                  <ScanOption key={w.name} icon="bi-usb-symbol" title={w.name} sub={`USB · ${w.port}${w.status && w.status !== "Normal" ? ` · ${w.status}` : ""}`}
                    selected={!isNetwork && s.printerName.trim() === w.name}
                    onSelect={() => setS((x) => ({ ...x, connection: "windows", printerName: w.name }))} />
                ))}
              </div>
            )}

            {/* USB'de tanınıyor ama Windows'a eklenmemiş — en sık kurulum adımı */}
            {scan?.ok && scan.unconfiguredUsb.length > 0 && (
              <div style={{ marginTop: 8, fontSize: ".76rem", color: "#1e3a8a", background: "#eff6ff", border: "1.5px solid #bfdbfe", borderRadius: 10, padding: "8px 10px", lineHeight: 1.55 }}>
                <b><i className="bi bi-usb-symbol me-1"></i>{scan.unconfiguredUsb.map((u) => u.name).join(", ")}</b> USB'de takılı ama Windows'a yazıcı olarak eklenmemiş.
                Bir kez ekleyin (sürücü indirmeye gerek yok):
                <ol style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                  <li>Ayarlar → Bluetooth ve cihazlar → Yazıcılar ve tarayıcılar → <b>Cihaz ekle</b> → "İstediğim yazıcı listelenmiyor" → <b>El ile ayarlarla yerel yazıcı ekle</b></li>
                  <li>Varolan bağlantı noktası: <b>USB001</b> (Sanal yazıcı bağlantı noktası)</li>
                  <li>Üretici: <b>Generic</b> → Yazıcı: <b>Generic / Text Only</b></li>
                  <li>Ad: <b>POS-80</b> → bitir, sonra burada <b>Yazıcı ara</b>'ya tekrar basın.</li>
                </ol>
              </div>
            )}

            {scan?.ok && scan.printers.length === 0 && usbPrinters.length === 0 && scan.unconfiguredUsb.length === 0 && (
              <div style={{ marginTop: 8, fontSize: ".76rem", color: "#92400e", lineHeight: 1.5 }}>
                <b>Yazıcı bulunamadı</b> ({scan.subnets.join(", ") || "ağ yok"} tarandı, USB'de de yok). Olası nedenler:
                <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                  <li>Yazıcı kapalı ya da kablosu takılı değil.</li>
                  <li>Yazıcının fabrika IP'si başka bir ağda (ör. 192.168.1.x / 192.168.123.x). Yazıcı kapalıyken <b>FEED</b> tuşuna basılı tutup açın; basılan test sayfasında IP yazar.</li>
                </ul>
              </div>
            )}

            {scan?.ok && otherWindowsPrinters.length > 0 && !isNetwork && (
              <div style={{ marginTop: 8, fontSize: ".72rem", color: "#6b7280" }}>
                Diğer Windows yazıcıları (fiş yazıcısı değilse seçmeyin): {otherWindowsPrinters.map((w) => w.name).join(", ")}
              </div>
            )}
          </div>
          )}

          <Field title="Kağıt genişliği">
            <Chips value={s.width} onChange={(v) => set("width", v)} options={[{ v: 48, label: "80 mm" }, { v: 32, label: "58 mm" }]} />
          </Field>

          <Field title="Türkçe karakter" help={isRawbt
            ? "Fişte ş, ğ, ı gibi harfler bozuk çıkıyorsa \"Kapalı\" seçin; harfler s, g, i olarak basılır."
            : "Fişte ş, ğ, ı gibi harfler bozuk çıkıyorsa \"Resim olarak bas\" seçin; her harf doğru basılır, baskı biraz yavaşlar."}>
            <Chips value={isRawbt && s.charset === "image" ? "tr" : s.charset} onChange={(v) => set("charset", v)}
              options={[
                { v: "tr", label: "Açık (CP857)" },
                { v: "ascii", label: "Kapalı" },
                ...(isRawbt ? [] : [{ v: "image" as const, label: "Resim olarak bas" }]),
              ]} />
          </Field>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Field title="Kopya sayısı">
              <Chips value={s.copies} onChange={(v) => set("copies", v)} options={[{ v: 1, label: "1" }, { v: 2, label: "2" }, { v: 3, label: "3" }]} />
            </Field>
            <Field title="Kağıdı kes">
              <Chips value={s.cut ? "y" : "n"} onChange={(v) => set("cut", v === "y")} options={[{ v: "y", label: "Evet" }, { v: "n", label: "Hayır" }]} />
            </Field>
          </div>

          <button type="button" onClick={() => setShowAdvanced((v) => !v)}
            style={{ alignSelf: "flex-start", background: "none", border: "none", padding: 0, color: "#6b7280", fontWeight: 700, fontSize: ".8rem", cursor: "pointer" }}>
            <i className={`bi ${showAdvanced ? "bi-chevron-down" : "bi-chevron-right"} me-1`}></i>Gelişmiş
          </button>
          {showAdvanced && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 120px", gap: 10 }}>
              <Field title="Yazdırma sunucusu adresi" help="Kasadaki bilgisayarda çalışan sunucu. Varsayılan: http://127.0.0.1:4000">
                <input style={input} value={s.serverUrl} placeholder={DEFAULT_PRINTER_SETTINGS.serverUrl}
                  onChange={(e) => set("serverUrl", e.target.value)} />
              </Field>
              <Field title="Kod sayfası" help="Türkçe: 13">
                <input style={input} type="number" min={0} max={255} value={s.codePage}
                  onChange={(e) => set("codePage", Number(e.target.value))} />
              </Field>
            </div>
          )}

          {/* Kontroller */}
          {isRawbt ? (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <button type="button" className="btn btn-sm btn-outline-dark" onClick={() => {
                printViaRawBT(buildReceiptBytes(TEST_RECEIPT, { width: s.width, charset: s.charset === "image" ? "tr" : s.charset, codePage: s.codePage, cut: s.cut }));
                setStatus({ kind: "ok", text: "Test fişi RawBT'ye gönderildi. Basılmadıysa RawBT'deki yazıcı IP'sini kontrol edin." });
              }}>
                <i className="bi bi-receipt me-1"></i>Test fişi yazdır
              </button>
            </div>
          ) : (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={!!busy} onClick={() => run("server")}>
              {busy === "server" ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-hdd-network me-1"></i>}Sunucuyu kontrol et
            </button>
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={!!busy || targetInvalid} onClick={() => run("printer")}>
              {busy === "printer" ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-plug me-1"></i>}Yazıcıya bağlan
            </button>
            <button type="button" className="btn btn-sm btn-outline-dark" disabled={!!busy || targetInvalid} onClick={() => run("test")}>
              {busy === "test" ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-receipt me-1"></i>}Test fişi yazdır
            </button>
          </div>
          )}

          {status && (
            <div style={{
              borderRadius: 10, padding: "9px 12px", fontSize: ".82rem", display: "flex", gap: 8,
              background: status.kind === "ok" ? "#f0fdf4" : "#fef2f2", color: status.kind === "ok" ? "#15803d" : "#b91c1c",
              border: `1.5px solid ${status.kind === "ok" ? "#bbf7d0" : "#fecaca"}`,
            }}>
              <i className={`bi ${status.kind === "ok" ? "bi-check-circle-fill" : "bi-exclamation-circle-fill"}`}></i>
              <span>{status.text}</span>
            </div>
          )}

          {!isRawbt && <div style={{ background: "#f8fafc", borderRadius: 10, padding: "10px 12px", fontSize: ".76rem", color: "#4b5563", lineHeight: 1.5 }}>
            <b>Nasıl çalışır?</b> Tarayıcı yazıcıya doğrudan bağlanamaz. Fiş, bu bilgisayarda arka planda çalışan
            <b> Mutfacık Yazıcı</b> programı üzerinden yazıcıya gider. Program kurulu değilse <code>yazici-kurulum\KURULUM.bat</code> dosyasını
            bir kez çalıştırın; Windows her açıldığında kendiliğinden başlar.
          </div>}
        </div>

        <div style={{ padding: "12px 22px 18px", display: "flex", justifyContent: "flex-end", gap: 8, borderTop: "1.5px solid #f1f5f9" }}>
          <button className="btn btn-light" onClick={onClose}>Vazgeç</button>
          <button className="btn btn-dark" onClick={save} disabled={targetInvalid}>
            <i className="bi bi-check-lg me-1"></i>Kaydet
          </button>
        </div>
      </div>
    </div>
  );
}
